// Shared constants and micro-components used across the domain detail tab files.
import React from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const TEAL = '#14B8A6';

export const ROLES = [
  { value: 'domain_admin', label: 'Admin (see all calls, manage settings)' },
  { value: 'domain_supervisor', label: 'Supervisor (see all calls, read-only)' },
  { value: 'domain_agent', label: 'Agent (own calls only)' },
];

export const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'calls', label: 'Calls' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'coaching', label: 'Coaching' },
  { id: 'intelligence', label: 'Intelligence' },
  { id: 'achievements', label: 'Achievements' },
  { id: 'users', label: 'Users' },
];

export const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const TIMEZONES = [
  'UTC', 'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane',
  'Australia/Perth', 'Pacific/Auckland', 'Asia/Singapore',
  'America/New_York', 'America/Los_Angeles', 'Europe/London',
];

export const COACHING_TYPES = [
  { value: 'manager_leadership', label: 'Management — Leadership' },
  { value: 'manager_other',      label: 'Management — Other' },
  { value: 'manager_general',    label: 'Management — General' },
  { value: 'customer_support',   label: 'Customer Support' },
  { value: 'customer_service',   label: 'Customer Service' },
  { value: 'it_technical',       label: 'IT Technical Support' },
  { value: 'it_support',         label: 'IT Support (Legacy)' },
  { value: 'sales_new',          label: 'Sales — New Business' },
  { value: 'sales_bdm',          label: 'Sales — BDM' },
  { value: 'sales_used',         label: 'Sales — Pre-owned' },
  { value: 'sales_parts',        label: 'Parts & Accessories' },
  { value: 'service_management', label: 'Service Management' },
  { value: 'admin_accounts',     label: 'Admin — Accounts' },
  { value: 'admin_other',        label: 'Admin — Other' },
  { value: 'admin',              label: 'Administration' },
  { value: 'practitioner',       label: 'Practitioner' },
  { value: 'general',            label: 'General (Catch-all)' },
  { value: 'other',              label: 'Other' },
];

// ── Stat card ───────────────────────────────────────────────────────────────────
export const StatCard = ({ label, value, color = 'text-white', onClick = null }) => (
  <div
    className={`bg-white/5 border border-white/10 rounded-xl p-4 transition-all ${
      onClick ? 'cursor-pointer hover:bg-white/10 hover:border-teal-500/30 group' : ''
    }`}
    onClick={onClick}
  >
    <p className={`text-2xl font-bold ${color} ${onClick ? 'group-hover:underline decoration-dotted' : ''}`}>{value}</p>
    <p className="text-white/30 text-xs mt-0.5">
      {label}{onClick ? <span className="text-teal-500/70 ml-1">→</span> : ''}
    </p>
  </div>
);

// ── Score badge ─────────────────────────────────────────────────────────────────
export const ScoreBadge = ({ score }) => {
  const color = score >= 80
    ? 'bg-emerald-500/20 text-emerald-300'
    : score >= 60
    ? 'bg-amber-500/20 text-amber-300'
    : 'bg-red-500/20 text-red-300';
  return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${color}`}>{score}</span>;
};

// ── Status badge ────────────────────────────────────────────────────────────────
export const StatusBadge = ({ status, gradingError }) => {
  const cfg = {
    analyzed:      { color: 'bg-emerald-500/20 text-emerald-300', icon: CheckCircle },
    escalated:     { color: 'bg-red-500/20 text-red-300', icon: XCircle },
    pending:       { color: 'bg-amber-500/20 text-amber-300', icon: Clock },
    no_transcript: { color: 'bg-slate-500/20 text-slate-400', icon: AlertCircle },
    skipped_short: { color: 'bg-white/5 text-white/30', icon: Clock },
  }[status] || { color: 'bg-white/10 text-white/40', icon: Clock };
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${cfg.color}`}
      title={gradingError || status}
    >
      <Icon className="w-3 h-3" />
      {status === 'no_transcript' ? 'no transcript' : status}
    </span>
  );
};

// ── AU phone normalisation ──────────────────────────────────────────────────────
export const normalizeAuPhone = (raw) => {
  if (!raw) return '—';
  let n = raw;
  try { n = decodeURIComponent(n); } catch (e) { console.debug('normalizeAuPhone: input was not URI-encoded, using raw', e?.message); /* keep raw */ }
  if (n.includes('@')) n = n.split('@')[0];
  if (!n) return '—';
  if (/^\d{1,6}$/.test(n)) return n;
  if (n.startsWith('+61') && n.length >= 12) return '0' + n.slice(3);
  if (/^61\d{9}$/.test(n)) return '0' + n.slice(2);
  if (/^[23478]\d{8}$/.test(n)) return '0' + n;
  return n;
};

// ── Agent display: "Name (ext 1234)" or "Ext 1234" when name unknown ───────────
// Priority: explicit call.extension field → agent_id as ext fallback.
// agentName comes from the Yabbit user directory when possible; otherwise
// shows extension only so the UI never prints a raw digit as a person's name.
export const formatAgent = (agentId, agentName, extension) => {
  const ext = (extension && String(extension)) || (agentId && String(agentId)) || '';
  const hasRealName = agentName &&
    agentName !== ext &&
    agentName !== agentId &&
    !/^Ext\s+/i.test(agentName) &&
    !/^[\d+\-() ]{5,}$/.test(agentName);
  if (hasRealName && ext) return `${agentName} (ext ${ext})`;
  if (hasRealName)        return agentName;
  if (ext)                return `Ext ${ext}`;
  return 'Unknown agent';
};
